import sanityClient from '@sanity/client'
import blocksToHtml from '@sanity/block-content-to-html'
import imageUrlBuilder from '@sanity/image-url'

const PROJECT_ID = 'sremz6cz'
const DATASET = 'production'
const client = sanityClient({
  projectId: PROJECT_ID,
  dataset: DATASET,
  apiVersion: '2021-03-25',
  useCdn: true,
})

const h = blocksToHtml.h

export default function useCMS() {
  const _imageUrlBuilder = imageUrlBuilder(client)

  const getArticles = async () => {
    const query = `*[_type == "post"] | order(publishedAt desc) {
      _id,
      title,
      publishedAt,
      slug,
      previewBody,
      mainImage,
      categories[]->{title,slug},
      author->{name,slug,image}
    }[0...50]
    `

    const articles = (await client.fetch(query)).map((article) => {
      const html = blocksToHtml({
        blocks: article.previewBody,
        projectId: PROJECT_ID,
        dataset: DATASET,
        serializers: {
          types: { block: ArticleListBlockRenderer },
        },
      })

      return { ...article, previewBody: html }
    })

    return articles
  }

  const ArticleListBlockRenderer = (props) => {
    const style = props.node.style || 'normal'

    if (/^h\d/.test(style)) {
      return h('h1', { className: 'text-2xl text-purple-500' }, props.children)
    }

    return style === 'blockquote'
      ? h('blockquote', { className: 'my-block-quote' }, props.children)
      : h(
          'p',
          { className: 'text-base text-gray-800 mt-4 mb-4' },
          props.children,
        )
  }

  const ButtonRenderer = (props) => {
    const a = h(
      'a',
      {
        className:
          'px-8 py-4 text-white bg-purple-500 rounded-full mx-auto text-semibold hover:bg-purple-800',
        href: props.node.url,
      },
      props.node.text,
    )

    return h(
      'div',
      {
        className: 'flex items-center',
      },
      a,
    )
  }

  const YouTubeEmbedRenderer = (props) => {
    const iFrame = h('iframe', {
      className: 'object-fill w-full h-48 md:h-80 rounded-lg my-2',
      src: props.node.url,
      title: props.node.title,
      frameBorder: 0,
      allow:
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture',
      allowFullScreen: true,
    })

    return h(
      'div',
      {
        className: 'flex items-center',
      },
      iFrame,
    )
  }

  const SingleArticleBlockRenderer = (props, getHeaderId) => {
    const style = props.node.style || 'normal'

    if (/^h\d/.test(style)) {
      const level = style.replace(/[^\d]/g, '')

      if (level === '1') {
        return h(
          'h1',
          {
            className:
              'text-3xl lg:text-4xl text-center text-gray-900 font-bold my-16 md:mb-20 md:mt-16 lg:mt-14 lg:mb-24',
          },
          props.children,
        )
      }

      if (level === '2') {
        return h(
          'h2',
          {
            id: getHeaderId ? getHeaderId(props.children) : undefined,
            className:
              'text-2xl lg:text-3xl text-gray-900 font-bold mt-24 mb-8',
          },
          props.children,
        )
      }

      if (level === '3') {
        return h(
          'h3',
          {
            className: 'text-xl text-gray-900 font-bold leading-5 mt-20 mb-7',
          },
          props.children,
        )
      }
    }

    return style === 'blockquote'
      ? h('blockquote', { className: 'my-block-quote' }, props.children)
      : h(
          'p',
          {
            className:
              'lg:text-lg text-gray-700 mt-8 font-normal leading-relaxed tracking-normal',
          },
          props.children,
        )
  }

  const getArticle = async (slug) => {
    const query = `*[_type == "post" && slug.current == "${slug}"] {
      _id,
      title,
      metaTitle,
      description,
      mainImage,
      publishedAt,
      slug,
      categories[]->{title,slug},
      author->{name,slug,image},
      body
    }
    `

    const h = blocksToHtml.h

    const ImageRenderer = (props) => {
      return h('img', {
        className: 'mt-8 w-full rounded-lg object-cover',
        src: blocksToHtml.getImageUrl(props),
        alt: props.node.alt,
      })
    }
    const ListRenderer = (props) =>
      h(
        'ul',
        { className: 'mt-6 mb-6 pl-6 pr-6 lg:pl-12 lg:pr-12' },
        props.children,
      )
    const ListItemRenderer = (props) =>
      h(
        'li',
        {
          className:
            'lg:text-lg text-gray-900 mb-4 mt-4 leading-relaxed tracking-normal',
          style: { 'list-style-type': 'disc' },
        },
        props.children,
      )

    const strong = (props) =>
      h('strong', { className: 'font-black' }, props.children)

    const link = (props) =>
      h(
        'a',
        {
          className: 'underline text-purple-500 font-bold',
          href: props.mark.href,
        },
        props.children,
      )

    const article = (await client.fetch(query)).map((article) => {
      const headers = []
      const html = blocksToHtml({
        blocks: article.body,
        projectId: PROJECT_ID,
        dataset: DATASET,
        imageOptions: { width: 640 * 2, fit: 'max' },
        serializers: {
          types: {
            block: (props) => {
              return SingleArticleBlockRenderer(props, (header) => {
                headers.push(header)

                return headers.length
              })
            },
            image: ImageRenderer,
            video: YouTubeEmbedRenderer,
            button: ButtonRenderer,
          },
          marks: { strong, link },
          list: ListRenderer,
          listItem: ListItemRenderer,
        },
      })

      return { ...article, body: html, headers }
    })[0]

    return article
  }

  const getFAQs = async () => {
    const h = blocksToHtml.h

    const QuestionBlockRenderer = (props) => {
      const style = props.node.style || 'normal'

      if (/^h\d/.test(style)) {
        return h(
          'h1',
          { className: 'text-2xl text-purple-500' },
          props.children,
        )
      }

      const a = props.children.filter((n) => n.nodeName === 'a').shift()

      if (a) {
        a.classList.add('underline')
      }

      return style === 'blockquote'
        ? h('blockquote', { className: 'my-block-quote' }, props.children)
        : h(
            'p',
            { className: 'text-sm text-purple-200 mt-4 mb-4' },
            props.children,
          )
    }

    const query = `*[_type == "question"] | order(number asc) {
      _id,
      title,
      number,
      answer,
    }[0...50]
    `

    const faqs = (await client.fetch(query)).map((question) => {
      const html = blocksToHtml({
        blocks: question.answer,
        projectId: PROJECT_ID,
        dataset: DATASET,
        serializers: { types: { block: QuestionBlockRenderer } },
      })

      return {
        ...question,
        id: question._id,
        question: question.title,
        answer: html,
      }
    })

    return faqs
  }

  const urlFor = (source) => {
    return _imageUrlBuilder.image(source)
  }

  const getAuthor = async (slug) => {
    const query = `*[_type == "author" && slug.current == "${slug}"] {
      _id,
      slug,
      name,
      image,
      bio
    }`

    const strong = (props) =>
      h('strong', { className: 'font-black' }, props.children)

    const link = (props) =>
      h(
        'a',
        {
          className: 'underline text-purple-500 font-bold',
          href: props.mark.href,
        },
        props.children,
      )

    const author = (await client.fetch(query)).map((author) => {
      const html = blocksToHtml({
        blocks: author.bio,
        projectId: PROJECT_ID,
        dataset: DATASET,
        serializers: {
          types: { block: SingleArticleBlockRenderer },
          marks: { strong, link },
        },
      })

      return {
        ...author,
        id: author._id,
        bio: html,
      }
    })

    return author[0]
  }

  const getCategory = async (slug) => {
    const query = `*[_type == "category" && slug.current == "${slug}"] {
      _id,
      slug,
      title,
      description,
    }`

    const strong = (props) =>
      h('strong', { className: 'font-black' }, props.children)

    const link = (props) =>
      h(
        'a',
        {
          className: 'underline text-purple-500 font-bold',
          href: props.mark.href,
        },
        props.children,
      )

    const category = (await client.fetch(query)).map((category) => {
      const html = blocksToHtml({
        blocks: category.description,
        projectId: PROJECT_ID,
        dataset: DATASET,
        serializers: {
          types: { block: SingleArticleBlockRenderer },
          marks: { strong, link },
        },
      })

      return {
        ...category,
        id: category._id,
        description: html,
      }
    })

    return category[0]
  }

  const getArticlesForAuthor = async (authorId) => {
    const query = `*[_type == "post" && author._ref == "${authorId}"]{
      _id,
      title,
      publishedAt,
      slug,
      previewBody,
      mainImage,
      categories[]->{title,slug},
      author->{name,slug}
    }[0...50]
    `

    const articles = (await client.fetch(query)).map((article) => {
      const html = blocksToHtml({
        blocks: article.previewBody,
        projectId: PROJECT_ID,
        dataset: DATASET,
        serializers: {
          types: { block: ArticleListBlockRenderer },
        },
      })

      return { ...article, previewBody: html }
    })

    return articles
  }

  const getArticlesForCategory = async (categoryId) => {
    const query = `*[_type == "post" && "${categoryId}" in categories[]._ref]{
      _id,
      title,
      publishedAt,
      slug,
      previewBody,
      mainImage,
      categories[]->{title,slug},
      author->{name,slug}
    }[0...50]
    `

    const articles = (await client.fetch(query)).map((article) => {
      const html = blocksToHtml({
        blocks: article.previewBody,
        projectId: PROJECT_ID,
        dataset: DATASET,
        serializers: {
          types: { block: ArticleListBlockRenderer },
        },
      })

      return { ...article, previewBody: html }
    })

    return articles
  }

  const getCategories = () => {
    const query = `*[_type == "category"] {
      _id,
      title,
      description,
      slug,
    }[0...50]
    `

    return client.fetch(query)
  }

  const getAuthors = () => {
    const query = `*[_type == "author"] {
      _id,
      slug,
    }[0...50]
    `

    return client.fetch(query)
  }

  const getSitemapEntries = () => {
    const query =
      '*[_type == "sitemapEntry"] | order(order asc) {_id, category, title, url}'

    return client.fetch(query)
  }

  return {
    getArticles,
    getArticle,
    getAuthor,
    getAuthors,
    getCategory,
    getCategories,
    getArticlesForAuthor,
    getArticlesForCategory,
    getFAQs,
    getSitemapEntries,
    urlFor,
  }
}
